import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Pause, Play, ChevronDown } from "lucide-react";
import { MaskedLines, Eyebrow, GoldButton, EASE } from "./motion";
import { IMAGES } from "../../data/content";

export default function Hero() {
  const ref = useRef(null);
  const videoRef = useRef(null);
  const [paused, setPaused] = useState(false);

  const togglePaused = () => {
    const next = !paused;
    setPaused(next);
    const v = videoRef.current;
    if (v) {
      if (next) v.pause();
      else v.play().catch(() => {});
    }
  };
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.85], [1, 0.1]);

  return (
    <section ref={ref} className="grain relative flex min-h-[100svh] items-end overflow-hidden bg-ink" aria-label="Hapo Group hero">
      <motion.div style={{ y }} className="absolute inset-0 scale-110">
        <video
          ref={videoRef}
          className="h-full w-full object-cover opacity-50"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster={IMAGES.heroHome}
          aria-hidden="true"
          data-testid="hero-video"
        >
          <source src="/assets/hero.mp4" type="video/mp4" />
        </video>
        <div className="ambient-layer" style={{ animationPlayState: paused ? "paused" : "running" }} />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/55 to-ink/25" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 w-full px-6 pb-32 pt-44 lg:px-12">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15, duration: 0.8 }}>
          <Eyebrow>Hapo Group · South Africa</Eyebrow>
        </motion.div>
        <MaskedLines
          as="h1"
          lines={["The Group", "That Connects."]}
          delay={0.25}
          className="mt-6 max-w-6xl text-[14vw] font-extrabold leading-[0.92] tracking-tighter text-white sm:text-8xl lg:text-[120px]"
        />
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.8, ease: EASE }}
          className="mt-8 max-w-xl text-lg font-medium text-white md:text-xl"
        >
          Innovating for a Smarter, More Sustainable Tomorrow
        </motion.p>
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.95, duration: 0.8, ease: EASE }}
          className="mt-4 max-w-xl text-sm leading-loose text-smoke md:text-base"
        >
          Hapo Group brings together technology, cloud &amp; AI, media, fintech, electronics manufacturing, green
          energy, consumer intelligence and business music under one South African holding company.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8, ease: EASE }}
          className="mt-10"
        >
          <GoldButton to="/companies" testId="hero-explore-companies">
            Explore Our Companies
          </GoldButton>
        </motion.div>
      </motion.div>

      <button
        type="button"
        data-testid="hero-pause-toggle"
        aria-pressed={paused}
        aria-label={paused ? "Play background motion" : "Pause background motion"}
        onClick={togglePaused}
        className="absolute bottom-8 right-6 z-20 flex h-11 w-11 items-center justify-center rounded-full border border-white/25 text-white transition-colors duration-300 hover:border-gold hover:text-gold lg:right-12"
      >
        {paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
      </button>

      <div className="absolute bottom-8 left-6 z-20 hidden items-center gap-3 text-smoke md:flex lg:left-12">
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}>
          <ChevronDown className="h-4 w-4 text-gold" />
        </motion.span>
        <span className="text-[10px] font-semibold uppercase tracking-[0.3em]">Scroll</span>
      </div>
    </section>
  );
}
